import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, fileStreamUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import ShareModal from "../components/ShareModal";
import AddToPlaylistModal from "../components/AddToPlaylistModal";
import { Heart, MessageCircle, Play, Share2, Send, Download, ListPlus, ArrowLeft, SkipBack, SkipForward, ListMusic } from "lucide-react";
import { toast } from "sonner";
import { EmbedPlayer, ProviderBadge } from "../components/EmbedPlayer";
import { TrackCard } from "../components/TrackCard";
import { VideoElement } from "../components/VideoElement";

export const TrackDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const playlistId = searchParams.get("playlist");
  const { user } = useAuth();
  const { playTrack, currentTrack, isPlaying, togglePlay, addToQueue } = usePlayer();
  const [track, setTrack] = useState(null);
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [showAddPlaylist, setShowAddPlaylist] = useState(false);
  const [downloadable, setDownloadable] = useState(false);
  const [playlist, setPlaylist] = useState(null);
  const [moreFromArtist, setMoreFromArtist] = useState([]);
  const [similar, setSimilar] = useState([]);

  const load = async () => {
    const [t, c, l] = await Promise.all([
      api.get(`/tracks/${id}`),
      api.get(`/tracks/${id}/comments`),
      api.get(`/tracks/${id}/liked`).catch(() => ({ data: { liked: false } })),
    ]);
    setTrack(t.data);
    setComments(c.data);
    setLiked(l.data.liked);
    setDownloadable(!!t.data.downloadable);
  };

  useEffect(() => { load(); }, [id]);

  // Charger "plus de cet artiste" et "similaires" dès qu'on a la track courante
  useEffect(() => {
    if (!track?.user_id) return;
    api.get("/tracks", { params: { artist_id: track.user_id } })
      .then(({ data }) => setMoreFromArtist((data || []).filter((t) => t.track_id !== id).slice(0, 12)))
      .catch(() => setMoreFromArtist([]));
    if (track.genre) {
      api.get("/tracks", { params: { genre: track.genre, limit: 24 } })
        .then(({ data }) => setSimilar((data || []).filter((t) => t.track_id !== id && t.user_id !== track.user_id).slice(0, 12)))
        .catch(() => setSimilar([]));
    }
  }, [track?.user_id, track?.genre, id]);

  // Charger la playlist si un contexte playlist est présent
  useEffect(() => {
    if (!playlistId) { setPlaylist(null); return; }
    api.get(`/playlists/${playlistId}`).then(({ data }) => setPlaylist(data)).catch(() => setPlaylist(null));
  }, [playlistId]);

  // Calcul index courant + voisins
  const plTracks = playlist?.tracks || [];
  const currentIdx = plTracks.findIndex((t) => t.track_id === id);
  const prevTrack = currentIdx > 0 ? plTracks[currentIdx - 1] : null;
  const nextTrack = currentIdx >= 0 && currentIdx < plTracks.length - 1 ? plTracks[currentIdx + 1] : null;

  const goToPlaylistTrack = (t) => {
    navigate(`/track/${t.track_id}?playlist=${playlistId}`);
  };

  const handleLike = async () => {
    if (!user) { window.location.href = "/login"; return; }
    const { data } = await api.post(`/tracks/${id}/like`);
    setLiked(data.liked);
    setTrack((t) => ({ ...t, likes_count: t.likes_count + (data.liked ? 1 : -1) }));
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) { window.location.href = "/login"; return; }
    if (!newComment.trim()) return;
    const { data } = await api.post(`/tracks/${id}/comments`, { content: newComment });
    setComments([data, ...comments]);
    setNewComment("");
  };

  const handleDownload = async () => {
    if (!track.downloadable) { toast.error("Téléchargement désactivé par l'artiste"); return; }
    try {
      const resp = await fetch(fileStreamUrl(track.file_path));
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = track.file_path.split(".").pop();
      a.download = `${track.artist_name} - ${track.title}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      api.post(`/tracks/${id}/download`).catch(() => {});
      toast.success("Téléchargement lancé");
    } catch { toast.error("Erreur de téléchargement"); }
  };

  const toggleDownloadable = async () => {
    const next = !downloadable;
    await api.patch(`/tracks/${id}/settings`, { downloadable: next });
    setDownloadable(next);
    setTrack((t) => ({ ...t, downloadable: next }));
    toast.success(next ? "Téléchargement activé" : "Téléchargement désactivé");
  };

  if (!track) return <div className="p-12 text-white/60">Chargement...</div>;
  const isCurrent = currentTrack?.track_id === track.track_id;
  const isOwner = user?.user_id === track.user_id;

  return (
    <div className="px-4 md:px-12 py-10 max-w-5xl mx-auto pb-32" data-testid="track-detail-page">
      {/* Bouton retour */}
      <button
        onClick={() => {
          if (window.history.length > 1) navigate(-1);
          else navigate("/explore");
        }}
        className="inline-flex items-center gap-2 mb-6 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/80 hover:text-white transition"
        data-testid="track-back-btn"
      >
        <ArrowLeft size={16} /> Retour
      </button>

      {/* Contexte playlist : nav précédent/suivant */}
      {playlist && plTracks.length > 0 && (
        <div className="mb-6 p-3 rounded-2xl bg-gradient-to-r from-brand-red/10 via-brand-orange/10 to-brand-yellow/10 border border-brand-orange/20 flex flex-wrap items-center gap-3" data-testid="playlist-nav">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <ListMusic size={16} className="text-brand-orange shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Lecture playlist</p>
              <Link
                to={`/playlist/${playlistId}`}
                className="text-sm font-bold text-white hover:text-brand-orange transition truncate block"
              >
                {playlist.title} · {currentIdx + 1}/{plTracks.length}
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => prevTrack && goToPlaylistTrack(prevTrack)}
              disabled={!prevTrack}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
              title={prevTrack ? `← ${prevTrack.title}` : "Début de la playlist"}
              data-testid="playlist-prev-btn"
            >
              <SkipBack size={16} />
            </button>
            <button
              onClick={() => nextTrack && goToPlaylistTrack(nextTrack)}
              disabled={!nextTrack}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-orange hover:bg-brand-red text-white font-bold text-sm disabled:opacity-30 disabled:cursor-not-allowed transition"
              title={nextTrack ? `→ ${nextTrack.title}` : "Fin de la playlist"}
              data-testid="playlist-next-btn"
            >
              Suivant <SkipForward size={16} />
            </button>
          </div>
        </div>
      )}
      {playlist && plTracks.length > 0 && track?.external_provider === "youtube" && nextTrack && (
        <p className="-mt-3 mb-4 text-[11px] text-brand-yellow/80 flex items-center gap-1.5 px-1">
          ✨ Auto-passage activé — à la fin de la vidéo, la suivante se lance toute seule.
        </p>
      )}

      {/* Embed externe plein largeur au-dessus (YouTube/Vimeo/TikTok/SoundCloud) */}
      {track.external_embed && (
        <div className="mb-8">
          <EmbedPlayer
            track={track}
            autoplay={!!playlistId}
            onEnded={nextTrack ? () => goToPlaylistTrack(nextTrack) : undefined}
          />
        </div>
      )}

      <div className="grid md:grid-cols-[320px_1fr] gap-8 mb-10">
        <div className="aspect-square rounded-2xl overflow-hidden bg-brand-elevated border border-white/10">
          {!track.external_embed && track.track_type === "video" ? (
            <VideoElement src={fileStreamUrl(track.file_path)} cover={track.cover_path ? fileStreamUrl(track.cover_path) : null} />
          ) : track.cover_url || track.cover_path ? (
            <img src={track.cover_url || fileStreamUrl(track.cover_path)} alt={track.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-red to-brand-yellow" />
          )}
        </div>
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2 flex items-center gap-2">
            {track.track_type === "video" ? "Vidéo" : track.track_type === "image" ? "Image" : "Audio"} · {track.genre}
            {track.external_provider && <ProviderBadge provider={track.external_provider} />}
          </p>
          <h1 className="font-display text-3xl md:text-5xl font-black tracking-tighter mb-3">{track.title}</h1>
          <Link to={`/artist/${track.user_id}`} className="text-lg text-brand-orange hover:text-brand-red transition-colors" data-testid="track-artist-link">
            {track.artist_name}
          </Link>
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="tag-pill">{track.country}</span>
            <span className="tag-pill">{track.language}</span>
            <span className="tag-pill">{track.plays} écoutes</span>
            {track.downloads > 0 && <span className="tag-pill">{track.downloads} téléchargements</span>}
          </div>
          {track.description && <p className="text-white/80 mt-5 max-w-2xl">{track.description}</p>}

          <div className="flex flex-wrap gap-2 mt-7">
            {track.track_type === "audio" && !track.external_embed && (
              <button
                onClick={() => isCurrent ? togglePlay() : playTrack(track)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="track-detail-play-btn"
              >
                <Play size={16} fill="currentColor" />
                {isCurrent && isPlaying ? "En lecture" : "Écouter"}
              </button>
            )}
            {track.external_url && (
              <a href={track.external_url} target="_blank" rel="noopener noreferrer" className="btn-ghost inline-flex items-center gap-2" data-testid="track-external-link">
                Ouvrir sur {track.external_provider}
              </a>
            )}
            <button onClick={handleLike} className={`btn-ghost inline-flex items-center gap-2 ${liked ? "text-brand-red" : ""}`} data-testid="track-like-btn">
              <Heart size={16} fill={liked ? "currentColor" : "none"} /> {track.likes_count}
            </button>
            <button onClick={() => setShowAddPlaylist(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="track-add-playlist-btn">
              <ListPlus size={16} /> Playlist
            </button>
            {track.track_type === "audio" && !track.external_embed && (
              <button onClick={() => { addToQueue(track); toast.success("Ajouté à la file"); }} className="btn-ghost inline-flex items-center gap-2" data-testid="track-add-queue-btn">
                <Send size={16} /> File d'attente
              </button>
            )}
            {track.downloadable && !track.external_embed && (
              <button onClick={handleDownload} className="btn-ghost inline-flex items-center gap-2" data-testid="track-download-btn">
                <Download size={16} /> Télécharger
              </button>
            )}
            <button onClick={() => setShowShare(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="track-share-btn">
              <Share2 size={16} /> Partager
            </button>
          </div>

          {isOwner && (
            <div className="mt-5 p-3 rounded-xl border border-white/10 bg-white/[0.03]" data-testid="track-owner-settings">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={downloadable} onChange={toggleDownloadable} className="accent-brand-red w-4 h-4" data-testid="toggle-downloadable" />
                <div>
                  <p className="text-sm font-semibold">Autoriser le téléchargement</p>
                  <p className="text-[11px] text-white/50">Vos fans pourront sauvegarder ce morceau sur leur appareil</p>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* PLUS DE CET ARTISTE */}
      {moreFromArtist.length > 0 && (
        <section className="mb-12" data-testid="more-from-artist-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-black">
              Plus de <Link to={`/artist/${track.user_id}`} className="text-brand-orange hover:text-brand-red transition">{track.artist_name}</Link>
            </h2>
            <Link to={`/artist/${track.user_id}`} className="text-sm text-white/60 hover:text-brand-orange transition" data-testid="see-all-artist-tracks">
              Voir tout →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {moreFromArtist.map((t) => (
              <TrackCard key={t.track_id} track={t} list={moreFromArtist} />
            ))}
          </div>
        </section>
      )}

      {/* VIDÉOS SIMILAIRES (même genre, autres artistes) */}
      {similar.length > 0 && (
        <section className="mb-12" data-testid="similar-tracks-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-black">À découvrir aussi</h2>
            {track.genre && <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 font-bold uppercase tracking-widest">{track.genre}</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {similar.map((t) => (
              <TrackCard key={t.track_id} track={t} list={similar} />
            ))}
          </div>
        </section>
      )}

      <section className="pb-10">
        <h2 className="font-display text-2xl font-black mb-6 inline-flex items-center gap-2">
          <MessageCircle size={22} /> Commentaires ({comments.length})
        </h2>        <form onSubmit={handleComment} className="flex items-center gap-2 mb-6 bg-white/5 border border-white/10 rounded-xl px-4 py-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={user ? "Ajouter un commentaire..." : "Connectez-vous pour commenter"}
            disabled={!user}
            className="flex-1 bg-transparent outline-none text-sm py-2"
            data-testid="comment-input"
          />
          <button type="submit" disabled={!user || !newComment.trim()} className="btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-40" data-testid="comment-submit-btn">
            <Send size={14} /> Publier
          </button>
        </form>

        <div className="space-y-4">
          {comments.map((c) => (
            <div key={c.comment_id} className="flex gap-3" data-testid={`comment-${c.comment_id}`}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-red to-brand-yellow flex items-center justify-center text-white font-bold shrink-0">
                {c.user_name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 bg-white/[0.03] border border-white/5 rounded-xl p-3">
                <p className="text-sm font-semibold">{c.user_name}</p>
                <p className="text-sm text-white/80 mt-0.5">{c.content}</p>
              </div>
            </div>
          ))}
          {comments.length === 0 && <p className="text-white/50 text-sm">Soyez le premier à commenter.</p>}
        </div>
      </section>

      <ShareModal open={showShare} onClose={() => setShowShare(false)} title={`${track.title} — ${track.artist_name}`} path={`/track/${track.track_id}`} />
      <AddToPlaylistModal open={showAddPlaylist} onClose={() => setShowAddPlaylist(false)} trackId={track.track_id} />
    </div>
  );
};

export default TrackDetailPage;
